import { css } from '@emotion/css';

// Dark theme colors
const colors = {
  background: '#1a1a1a',
  backgroundLight: '#2c2c2c',
  textPrimary: '#ffffff',
  textSecondary: '#eaeaea',
  accent: '#007bff',
  accentHover: '#0069d9',
  accentSubtle: '#1a365d',  // Subtle blue for not enough categories
  accentSubtleHover: '#234876',
  border: '#444444',
  borderHover: '#666666',
  disabled: '#555555',
  error: '#da8a8a',  // Less dramatic red color
  dislike: '#ff6b6b',
  neutral: '#ffc107',
  like: '#4caf50',
};

export const onboardingContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${colors.background};
  color: ${colors.textPrimary};
  min-height: 100vh;

  
`;

export const itemRankingOnboardingContainer = css`
 ${onboardingContainer}
  @media (max-width: 769px) {
    padding: 0;
    height: 100vh;
    // overflow: hidden;
  }
`

export const onboardingTitle = css`
  font-size: 1.7rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  text-align: center;
  color: ${colors.textPrimary};
`;

export const onboardingSubtitle = css`
  font-size: 1.1rem;
  padding: 0 5px;
  margin-bottom: 2rem;
  text-align: center;
  color: ${colors.textSecondary};
  @media (max-width: 769px) {
    margin-bottom: 12px;
    width: 80%;
  }
`;

export const categoriesContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 2rem;
`;

export const categoryGroupContainer = css`
  margin-bottom: 1.5rem;
`;

export const categoryGroupTitle = css`
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: ${colors.textPrimary};
`;

export const categoriesGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

export const categoryButton = css`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid ${colors.border};
  background-color: ${colors.backgroundLight};
  color: ${colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  text-align: left;
  
  &:hover {
    background-color: #2a2a2a;
  }
`;

export const categoryButtonSelected = css`
  background-color: ${colors.accent};
  color: white;
  border-color: ${colors.accentHover};
  
  &:hover {
    background-color: ${colors.accentHover};
  }
`;

export const buttonContainer = css`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 3rem;
`;

export const continueButton = (hasEnoughCategories: boolean) => css`
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  background-color: ${hasEnoughCategories ? colors.accent : colors.accentSubtle};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${hasEnoughCategories ? colors.accentHover : colors.accentSubtleHover};
  }
  
  &:disabled {
    background-color: ${colors.disabled};
    cursor: not-allowed;
  }
`;

export const errorMessage = css`
  color: ${colors.error};
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 500;
`;

// Step indicator used in both CategorySelection and ItemRanking
export const stepIndicator = css`
  background-color: #2d2d2d;
  color: #e0e0e0;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 769px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
    margin-top: 0.5rem;
  }
`;

// Styles for ItemRanking component
export const progressContainer = css`
  width: 92%;
  max-width: 600px;
  background: #333333;
  border-radius: 8px;
  height: 10px;
  overflow: visible; /* Changed from hidden to visible */
  margin: 1rem auto;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
  display: block; /* Ensure it's a block element */
  position: relative;
  opacity: 1 !important; /* Force visibility */
  z-index: 10; /* Ensure it's above other elements */
  
  @media (max-width: 769px) {
    height: 8px;
    margin: 5px auto;
  }
`;

export const progressBar = css`
  display: block;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #4776E6 0%, #8E54E9 100%);
  border-radius: 8px;
  transition: none; /* Disable transitions temporarily */
  opacity: 1 !important; /* Force visibility */
  min-width: 5px; /* Always show at least a small amount */
  z-index: 11; /* Above the container */
`;

export const progressText = css`
  font-size: 0.875rem;
  color: #a0a0a0;
  text-align: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 769px) {
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }
`;

export const loadingSpinner = css`
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid #8E54E9;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 2rem auto;
  
  @media (max-width: 769px) {
    width: 32px;
    height: 32px;
    border-width: 3px;
    border-top-width: 3px;
    margin: 1.5rem auto;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const completionMessage = css`
  text-align: center;
  margin: 2rem 0;
  
  @media (max-width: 769px) {
    margin: 1.5rem 0;
    font-size: 0.95rem;
  }
`;

export const itemContainer = css`
  width: 100%;
  border-radius: 12px;
  overflow: visible; /* Changed from auto to visible to prevent nested scrolling */
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
  
  @media (max-width: 769px) {
    padding-bottom: 0.5rem;
    margin: 0;
    border-radius: 0;
  }

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  margin-bottom: 1.5rem;
  border: 1px solid #333;
`;

export const ratingContainer = css`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  background-color: #1a1a1a;
  border-top: 1px solid #333;
  margin-top: auto;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  
  @media (max-width: 769px) {
    position: fixed; /* Use fixed positioning on mobile */
    padding: 1rem;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
  }
`;

export const ratingButton = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0.75rem 0.5rem;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #2d2d2d;
  color: #e0e0e0;
  transition: all 0.2s ease;
  cursor: pointer;
  flex: 1;
  font-size: 0.9rem;
  min-height: 70px;
  min-width: 90px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 769px) {
    padding: 0.5rem 0.25rem;
    font-size: 0.85rem;
    min-height: 60px;
    
    &:hover {
      transform: none;
    }
  }
`;

export const dislikeButton = css`
  background-color: #2d2d2d;
  border-color: ${colors.dislike};
  color: ${colors.dislike};
  
  &:hover {
    background-color: #2d2d2d;
    border-width: 2px;
  }
`;

export const neutralButton = css`
  background-color: #2d2d2d;
  border-color: ${colors.neutral};
  color: ${colors.neutral};
  
  &:hover {
    background-color: #2d2d2d;
    border-width: 2px;
  }
`;

export const likeButton = css`
  background-color: #2d2d2d;
  border-color: ${colors.like};
  color: ${colors.like};
  
  &:hover {
    background-color: #2d2d2d;
    border-width: 2px;
  }
`;

export const buttonIcon = css`
  font-size: 1.25rem;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const buttonText = css`
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
`;

export const errorText = css`
  color: ${colors.dislike};
  background-color: rgba(255, 107, 107, 0.1);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border-left: 4px solid ${colors.dislike};
  margin: 1rem 0;
  width: 100%;
  font-size: 0.9rem;
`;
